import React, { useState, useEffect } from 'react';
import {
    Box, IconButton, Drawer, List, ListItem, ListItemButton, ListItemText, Divider, Typography
} from "@mui/material";
import MenuIcon from '@mui/icons-material/Menu';
import AirplaneTicketIcon from '@mui/icons-material/AirplaneTicket';
import BaseHeader from '../BaseHeader';
import HelpButton from '../HelpButton';
import TagSearchSelect from '../TagSearchSelect';
import ErrorDialog from '../../dialogs/ErrorDialog';
import { canCreateOwnerRoom, getOwnerRoomCreateDeniedMessage } from '../../../common/ownerRoomPermission';

function RoomHeader({ user, drawerOnlyOnMobile = false }) {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 1100);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [ownerCreateDeniedDialogVisible, setOwnerCreateDeniedDialogVisible] = useState(false);
    const userName = user?.name ?? '';
    const ticketBalance = user ? (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <AirplaneTicketIcon sx={{ fontSize: 16 }} />
            <span>参加チケット {user.ticket}枚</span>
        </Box>
    ) : '';

    const linkBoxSx = {
        cursor: 'pointer',
        borderRadius: '3px',
        padding: '8px',
        transition: 'background-color 0.2s ease, color 0.2s ease',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.12)',
        },
        '&:active': {
            backgroundColor: 'rgba(255, 255, 255, 0.24)',
        },
        '&:focus-visible': {
            outline: '2px solid #ffffff',
            outlineOffset: '2px',
        },
    };

    const drawerLinkSx = {
        padding: '8px 16px',
        width: '100%',
        cursor: 'pointer',
        borderRadius: '4px',
        transition: 'background-color 0.2s ease',
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
        },
        '&:active': {
            backgroundColor: 'rgba(0, 0, 0, 0.16)',
        },
        '&:focus-visible': {
            outline: '2px solid #000000',
            outlineOffset: '2px',
        },
    };


    const drawerOnlyIconSx = {
        position: 'fixed',
        top: '8px',
        right: '8px',
        zIndex: 1200,
        width: '44px',
        height: '44px',
        color: '#111827',
        backgroundColor: 'rgba(255, 255, 255, 0.92)',
        border: '1px solid rgba(17, 24, 39, 0.12)',
        boxShadow: '0 6px 18px rgba(0, 0, 0, 0.18)',
        '&:hover': {
            backgroundColor: '#ffffff',
        },
    };

    const drawerActionSx = {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 16px',
    };

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 1100);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const navigate = (path) => {
        window.location.href = path;
    };

    const openOwnerCreatePage = () => {
        if (user && !canCreateOwnerRoom(user)) {
            setOwnerCreateDeniedDialogVisible(true);
            return;
        }

        navigate('./owner');
    };

    const closeDrawer = () => setDrawerOpen(false);

    const navigateFromDrawer = (path) => {
        closeDrawer();
        navigate(path);
    };

    const openOwnerCreatePageFromDrawer = () => {
        closeDrawer();
        openOwnerCreatePage();
    };

    const drawerContents = (
    <Box sx={{ width: 280 }} role="presentation">
        <Box sx={{ padding: '16px 16px 8px' }}>
            <Typography variant="subtitle2" color="text.secondary">
                メニュー
            </Typography>
        </Box>
        <Box sx={drawerActionSx}>
            <Box
                onClick={() => navigateFromDrawer('./')}
                sx={{ ...drawerLinkSx, width: '44px', textAlign: 'center', padding: '8px' }}
            >
                TOP
            </Box>
            <Box sx={{ flex: 1 }}>
                <TagSearchSelect />
            </Box>
            <HelpButton />
        </Box>
        <Divider />
        <List>
        <ListItem disablePadding>
            <ListItemButton onClick={() => navigateFromDrawer('./mypage')}>
                <ListItemText
                    primary={userName}
                    secondary={ticketBalance}
                />
            </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
            <ListItemButton onClick={openOwnerCreatePageFromDrawer}>
                <ListItemText primary="ベットルームの作成" />
            </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
            <ListItemButton onClick={() => navigateFromDrawer('./ranking')}>
                <ListItemText primary="ランキング" />
            </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
            <ListItemButton onClick={() => navigateFromDrawer('./search')}>
                <ListItemText primary="検索" />
            </ListItemButton>
        </ListItem>
        </List>
    </Box>
    );


    if (isMobile && drawerOnlyOnMobile) {
        return (
            <>
                <IconButton
                    color="inherit"
                    aria-label="menu"
                    onClick={() => setDrawerOpen(true)}
                    sx={drawerOnlyIconSx}
                >
                    <MenuIcon />
                </IconButton>
                <Drawer anchor="right" open={drawerOpen} onClose={closeDrawer}>
                    {drawerContents}
                </Drawer>
                <ErrorDialog
                    isVisible={ownerCreateDeniedDialogVisible}
                    onClose={() => setOwnerCreateDeniedDialogVisible(false)}
                    message={getOwnerRoomCreateDeniedMessage(user)}
                />
            </>
        );
    }

    return (
        <>
            <BaseHeader>
                {isMobile ? (
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
                            <Box onClick={() => navigate('./')} sx={linkBoxSx}>TOP</Box>
                            <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                                <TagSearchSelect />
                                <HelpButton />
                                <IconButton
                                    color="inherit"
                                    aria-label="menu"
                                    onClick={() => setDrawerOpen(true)}
                                    sx={{ flexShrink: 0 }}
                                >
                                    <MenuIcon />
                                </IconButton>
                            </Box>
                        </Box>
                        <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
                            {drawerContents}
                        </Drawer>
                    </>
                ) : (
                    <>
                        <Box onClick={() => navigate('./')} sx={{ ...linkBoxSx, width: '40px' }}>TOP</Box>
                        <Box onClick={openOwnerCreatePage} sx={{ ...linkBoxSx, width: '175px' }}>ベットルームの作成</Box>
                        <Box onClick={() => navigate('./ranking')} sx={{ ...linkBoxSx, width: '90px' }}>ランキング</Box>
                        <Box onClick={() => navigate('./search')} sx={{ ...linkBoxSx, width: '80px' }}>検索</Box>
                        <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                            <TagSearchSelect />
                            <HelpButton />
                            <Box onClick={() => navigate('./mypage')} sx={{ ...linkBoxSx, width: '160px', padding: '6px 8px' }}>
                                <div>{userName}</div>
                                <div>{ticketBalance}</div>
                            </Box>
                        </Box>
                    </>
                )}
            </BaseHeader>
            <ErrorDialog
                isVisible={ownerCreateDeniedDialogVisible}
                onClose={() => setOwnerCreateDeniedDialogVisible(false)}
                message={getOwnerRoomCreateDeniedMessage(user)}
            />
        </>
    );
}

export default RoomHeader;
